import { useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useLanguageSwitcher from "../../hooks/useLanguageSwitcher.js";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useScrollToHomeSection from "../../hooks/useScrollToHomeSection.js";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";
import TurnstileWidget from "../common/TurnstileWidget";
import {
  trackCTA,
  trackLead,
  trackNewsletterSubscribed,
} from "../../lib/analytics";
import {
  GUIDES_INDEX_PATH,
  homeHrefFor,
  rendersHomeSections,
} from "../../lib/navScope.js";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000";
const TURNSTILE_SITE_KEY = (
  import.meta.env.VITE_TURNSTILE_SITE_KEY || ""
).trim();

const Footer = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  // Language is a URL, not a piece of local state — see the hook.
  const { language, switchTo } = useLanguageSwitcher();
  const analyticsContext = useAnalyticsContext();

  // Internal links resolve to the language of the page they are rendered on;
  // see `useLocalizedHref` for why they fall back to English rather than
  // prefixing paths that have no localized page.
  const localize = useLocalizedHref();
  const homeHref = localize(homeHrefFor());

  // Mirror the navbar's registration destination. The app's guards gracefully
  // re-route an already-signed-in user, so this is safe regardless of session.
  const registerHref = `${import.meta.env.VITE_APP_URL}/couple/create-event`;

  const [nlEmail, setNlEmail] = useState("");
  const [nlStatus, setNlStatus] = useState("idle"); // idle | sending | success | error
  const [nlMessage, setNlMessage] = useState("");
  const [nlTurnstileToken, setNlTurnstileToken] = useState(null);

  const handleNlTurnstileToken = useCallback((token) => {
    setNlTurnstileToken(token);
  }, []);

  const handleNewsletterSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setNlStatus("sending");
      setNlMessage("");

      const genericError = t("footer.newsletter.genericError");

      try {
        // Normalize i18next locale ("enUS"/"elGR" or "en"/"el") down to the
        // BE's two-letter enum so the confirmation email arrives in the
        // current page language.
        const lang = (i18n.language || "").toLowerCase().startsWith("el")
          ? "el"
          : "en";
        const payload = {
          email: nlEmail,
          website: "", // honeypot — must be empty
          lang,
          ...(TURNSTILE_SITE_KEY && nlTurnstileToken
            ? { turnstileToken: nlTurnstileToken }
            : {}),
        };
        const res = await fetch(`${API_URL}/api/newsletter`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(genericError);
        }

        setNlStatus("success");
        setNlMessage("");
        setNlEmail("");
        // Conversion signal — only after the server confirms the subscription
        // (2xx), never on submit intent.
        trackNewsletterSubscribed(analyticsContext);
      } catch {
        setNlStatus("error");
        setNlMessage(genericError);
      }
    },
    [nlEmail, nlTurnstileToken, t, i18n, analyticsContext],
  );

  // See Navbar: same three-line race, written a third time, now shared.
  const scrollToFeatures = useScrollToHomeSection("features");
  const scrollToStory = useScrollToHomeSection("how-it-works");
  const scrollToPricing = useScrollToHomeSection("pricing");

  return (
    <div className="section foot">
      <div className="container footer">
        <a
          href={homeHref}
          onClick={(e) => {
            if (rendersHomeSections(location.pathname)) {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          aria-current="page"
          className="footer-brand w-inline-block w--current"
        >
          <img
            src="/assets/logo/full-logo/svg/Black logo - no background.svg"
            alt={t("nav.logo.footerAlt")}
            loading="lazy"
            className="footer-logo"
          />
        </a>
        <div className="footer-content">
          <div className="footer-item navi" style={{ flexDirection: "column" }}>
            <div className="sub-header">{t("footer.explore")}</div>
            <div className="footer-nav-column">
              <a
                href="#features"
                onClick={scrollToFeatures}
                className="footer-nav"
              >
                {t("nav.links.experience")}
              </a>
              <a
                href="#how-it-works"
                onClick={scrollToStory}
                className="footer-nav"
              >
                {t("nav.links.story")}
              </a>
              <a
                href="#pricing"
                onClick={scrollToPricing}
                className="footer-nav"
              >
                {t("nav.links.pricing")}
              </a>
              <a href={localize("/digital-wedding-invitation")} className="footer-nav">
                {t("nav.links.digitalInvitation")}
              </a>
              <a href={localize("/wedding-rsvp")} className="footer-nav">
                {t("nav.links.weddingRsvp")}
              </a>
              <a href={localize("/wedding-seating")} className="footer-nav">
                {t("nav.links.weddingSeating")}
              </a>
              <a href={localize("/wedding-photo-sharing")} className="footer-nav">
                {t("nav.links.photoSharing")}
              </a>
              <a href={localize("/wedding-planning")} className="footer-nav">
                {t("nav.links.weddingPlanning")}
              </a>
              <a href={localize("/contact")} className="footer-nav">
                {t("nav.links.contact")}
              </a>
              {/* Guides live here and only here. They are secondary content —
                  a reader arrives at one from search or from the product page
                  it belongs to, never from the global header — so the footer
                  carries a single destination rather than a directory. */}
              <a href={localize(GUIDES_INDEX_PATH)} className="footer-nav">
                {t("nav.links.guides")}
              </a>
              <a
                href={registerHref}
                className="footer-nav"
                onClick={() => {
                  trackCTA("footer", {
                    ...analyticsContext,
                    ctaName: "register",
                  });
                  trackLead("footer", {
                    ...analyticsContext,
                    ctaName: "register",
                  });
                }}
              >
                {t("nav.cta.register")}
              </a>
            </div>
          </div>
          <div className="footer-item new">
            <div className="news-wrap">
              <div className="sub-header">{t("footer.newsletter.title")}</div>
              <h4 className="heading-6">{t("footer.newsletter.heading")}</h4>
              <div className="form-block">
                {nlStatus !== "success" ? (
                  <form
                    id="email-form"
                    name="email-form"
                    data-name="Email Form"
                    onSubmit={handleNewsletterSubmit}
                  >
                    {/*
                     * Keep the Webflow `.news-form` row (input + absolute arrow
                     * button) self-contained, then stack the Turnstile widget
                     * below it as a sibling. The widget must NOT be a flex child
                     * of `.news-form`, or it lands on top of the email input.
                     */}
                    <div className="news-form">
                      <input
                        className="text-field w-input"
                        maxLength="254"
                        name="email"
                        placeholder={t("footer.newsletter.emailPlaceholder")}
                        type="email"
                        id="newsletter-email"
                        required
                        value={nlEmail}
                        onChange={(e) => setNlEmail(e.target.value)}
                        autoComplete="email"
                      />
                      {/* Honeypot — hidden from real users, filled by bots */}
                      <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        style={{
                          position: "absolute",
                          left: "-9999px",
                          opacity: 0,
                          height: 0,
                          width: 0,
                        }}
                      />
                      <input
                        type="submit"
                        className="form-button w-button"
                        value=""
                        disabled={
                          nlStatus === "sending" ||
                          (!!TURNSTILE_SITE_KEY && !nlTurnstileToken)
                        }
                      />
                    </div>
                    {TURNSTILE_SITE_KEY && (
                      <div style={{ marginTop: "16px" }}>
                        <TurnstileWidget
                          siteKey={TURNSTILE_SITE_KEY}
                          onToken={handleNlTurnstileToken}
                        />
                      </div>
                    )}
                  </form>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "12px",
                      padding: "24px 16px",
                      border: "1px solid #d5d0cb",
                      borderRadius: "6px",
                    }}
                  >
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ color: "#6b705c" }}
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    <p
                      style={{
                        fontSize: "14px",
                        lineHeight: "1.6",
                        color: "#555",
                        textAlign: "center",
                        margin: 0,
                        fontStyle: "italic",
                      }}
                    >
                      {t("footer.newsletter.successMessage")}
                    </p>
                  </div>
                )}
                {nlStatus === "error" && (
                  <p
                    style={{
                      fontSize: "13px",
                      lineHeight: "1.5",
                      color: "#a0522d",
                      textAlign: "center",
                      margin: "10px 0 0",
                    }}
                  >
                    {nlMessage}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="footer-item">
            <div className="footer-contact-div">
              <div className="sub-header">{t("footer.contact.title")}</div>
              <a
                href="mailto:hello@agapae.gr?subject=Agapa%C3%A9%20-%20Inquiry"
                target="_blank"
                rel="noreferrer"
                className="footer-contact-link w-inline-block"
              >
                <div>{t("footer.contact.email")}</div>
              </a>
            </div>
          </div>
          <div
            className="footer-item bottom"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div className="social-wrap">
              <a
                href="https://www.instagram.com/agapae_official/"
                target="_blank"
                rel="noreferrer"
                className="social-link w-inline-block"
              >
                <img
                  src="/assets/photos/Social-Icon-IG.svg"
                  alt={t("footer.social.instagramAlt")}
                  loading="lazy"
                  className="social-icon"
                />
              </a>
              <a
                href="https://www.facebook.com/agapae.official/"
                target="_blank"
                rel="noreferrer"
                className="social-link w-inline-block"
              >
                <img
                  src="/assets/photos/Social-Icon-FB.svg"
                  alt={t("footer.social.facebookAlt")}
                  loading="lazy"
                  className="social-icon"
                />
              </a>
            </div>
            <div
              className="pp-wrap"
              style={{ width: "auto", gap: "16px", flexWrap: "wrap" }}
            >
              <a
                href={localize("/terms")}
                className="footer-contact-link is-small w-inline-block"
              >
                <div>{t("footer.legal.terms")}</div>
              </a>
              <a
                href={localize("/privacy-policy")}
                className="footer-contact-link is-small w-inline-block"
              >
                <div>{t("footer.legal.privacy")}</div>
              </a>
              <a
                href={localize("/cookie-policy")}
                className="footer-contact-link is-small w-inline-block"
              >
                <div>{t("footer.legal.cookies")}</div>
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="copyright-div">
        <div className="rbq-wrap">
          <div className="copyright-text">
            {t("footer.copyright", { year: new Date().getFullYear() })}
            <br />
          </div>
          <div className="footer-lang">
            <button
              type="button"
              onClick={() => switchTo("en")}
              className={
                language === "en"
                  ? "footer-lang-active"
                  : "footer-lang-disabled"
              }
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                font: "inherit",
                color: "inherit",
              }}
            >
              EN
            </button>
            <span className="footer-lang-separator">|</span>
            <button
              type="button"
              onClick={() => switchTo("el")}
              className={
                language === "el"
                  ? "footer-lang-active"
                  : "footer-lang-disabled"
              }
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                font: "inherit",
                color: "inherit",
              }}
            >
              GR
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Footer;
